import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ROUTE_PATHS } from "@/lib/index";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CreditRepair from "@/pages/CreditRepair";
import TaxStrategy from "@/pages/TaxStrategy";
import BusinessFunding from "@/pages/BusinessFunding";
import GoalRoadmap from "@/pages/GoalRoadmap";
import CreditEducation from "@/pages/CreditEducation";
import AdminPanel from "@/pages/AdminPanel";
import PackageDemo from "@/pages/PackageDemo";

const queryClient = new QueryClient();

function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HashRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* ── Public Marketing Routes ── */}
            <Route path={ROUTE_PATHS.HOME} element={<Layout><Home /></Layout>} />
            <Route path={ROUTE_PATHS.SERVICES} element={<Layout><Services /></Layout>} />
            <Route path={ROUTE_PATHS.PRICING} element={<Layout><Pricing /></Layout>} />
            <Route path={ROUTE_PATHS.ABOUT} element={<Layout><About /></Layout>} />
            <Route path={ROUTE_PATHS.CONTACT} element={<Layout><Contact /></Layout>} />
            <Route path={ROUTE_PATHS.LOGIN} element={<Login />} />

            {/* ── Package Demo Routes (public) ── */}
            <Route path="/demo/*" element={<Layout><PackageDemo /></Layout>} />

            {/* ── Dashboard Routes (open – no login required) ── */}
            <Route path={ROUTE_PATHS.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTE_PATHS.CREDIT_REPAIR} element={<CreditRepair />} />
            <Route path={ROUTE_PATHS.TAX_STRATEGY} element={<TaxStrategy />} />
            <Route path={ROUTE_PATHS.BUSINESS_FUNDING} element={<BusinessFunding />} />
            <Route path={ROUTE_PATHS.GOAL_ROADMAP} element={<GoalRoadmap />} />
            <Route path={ROUTE_PATHS.CREDIT_EDUCATION} element={<CreditEducation />} />

            {/* ── Admin Portal (login required + admin role) ── */}
            <Route path={ROUTE_PATHS.ADMIN} element={
              <ProtectedRoute requireAdmin><AdminPanel /></ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </HashRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
